<template>
  <section id="account-show-page">
    <page-header class="mb-8" :title="accountName" :breadcrumbs="breadcrumbs" />
    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      class="border-bottom-grey"
    >
      <v-tab
        class="px-12 subtitle-2 text-dark-grey font-weight-large"
        active-class="setting-active-tab"
        v-for="({ name, query, elementId }, i) in tabsName"
        :id="elementId"
        v-text="name"
        :key="i"
        @click="onTabChange(query, i)"
      >
      </v-tab>
    </v-tabs>
    <v-row class="d-flex justify-center">
      <v-col cols="12">
        <v-tabs-items v-model="activeTab" class="border-bottom-2 transparent">
          <v-tab-item v-for="itemComponent in tabItems" :key="itemComponent">
            <keep-alive>
              <component
                :user="accountDetails"
                :is="itemComponent"
                :is-loading="isLoading"
                @set-account="setAccountName"
              />
            </keep-alive>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import AccountProfile from "./AccountProfile.vue";
import TabsNavigator from "@/mixins/TabsNavigator.mixin";
import PageHeader from "@/components/shared/PageHeader.vue";

import AccountUsers from "./AccountUsers.vue";
import { isEmpty } from "@/utils/common.utils";
import { mapGetters, mapActions } from "vuex";

/**
 * Account show page
 * @description Displays details of an account
 */
export default {
  name: "Account",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [TabsNavigator],
  /**
  |--------------------------------------------------
  | Component
  |--------------------------------------------------
  */
  components: {
    PageHeader,
    AccountUsers,
    AccountProfile,
  },
  /**
  |--------------------------------------------------
  | Data Properties
  |--------------------------------------------------
  */
  data() {
    return {
      accountName: "",
      activeTab: 0,
      isLoading: false,
      tabsName: [
        {
          name: "Profile",
          query: "profile",
          elementId: "account_profile",
        },
        {
          name: "Users",
          query: "users",
          elementId: "users",
        },
      ],
      tabItems: ["AccountProfile", "AccountUsers"],
      accountDetails: {},
      isMounted: false,
    };
  },
  /**
  |--------------------------------------------------
  | Computed properties
  |--------------------------------------------------
  */
  computed: {
    /**
     * Maps vuex store getters in the component
     */
    ...mapGetters({
      account: "account/account",
    }),
    /**
     * Determines account details heve to be fetched or not
     */
    fetchAccountDetails() {
      return this.activeTab === 1;
    },
    /**
     * Breadcrumbs of the show page
     */
    breadcrumbs() {
      return [
        {
          to: this.$paths.accounts,
          type: this.$appConfig.breadcrumbs.types.route,
          title: this.$dictionary.app.account.index.title,
        },
        {
          type: this.$appConfig.breadcrumbs.types.route,
          title: this.accountName,
        },
      ];
    },
  },
  /**
  |--------------------------------------------------
  | Watches component properties
  |--------------------------------------------------
  */
  watch: {
    /**
     * Fetches account detauls if fetchAccountDetails is true and component is mounted
     */
    fetchAccountDetails(val) {
      if (val && this.isMounted) this.getAccountDetails();
    },
  },
  /**
  |--------------------------------------------------
  | Method
  |--------------------------------------------------
  */
  methods: {
    /**
     * Maps vuex actions in the component
     */
    ...mapActions({
      getAccount: "account/getAccount",
    }),
    /**
     * Fetches details of an account
     */
    async getAccountDetails() {
      try {
        this.isLoading = true;
        const { id: account_id } = this.$route.params;

        await this.getAccount({ account_id });
        this.setAccountName(this.account);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Sets account details and account name to be shown in the breadcrumb
     */
    setAccountName(account) {
      this.accountDetails = { ...account };
      this.accountName = !isEmpty(account) ? account?.name : "";
    },
  },
  /**
  |--------------------------------------------------
  | Created lifecycle hook
  |--------------------------------------------------
  */
  created() {
    this.fetchAccountDetails && this.getAccountDetails();
    this.isMounted = true;
  },
};
</script>
